import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { ArrowRight, Twitter, Linkedin, Copy, Check, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const Footer = () => {
  // --------- LIVE DEMO STATE ---------
  const [liveInput, setLiveInput] = useState('');
  const [demoState, setDemoState] = useState('idle'); // idle, processing, resolved
  const [mockResults, setMockResults] = useState([]);

  const handleParseDemo = () => {
    if (!liveInput.trim()) return;
    setDemoState('processing');

    setTimeout(() => {
      const text = liveInput.toLowerCase();
      const results = [];

      // Pattern 1: Appointments/Dentist/Doctor + Person
      if (text.includes('reschedule') || text.includes('appointment') || text.includes('dentist') || text.includes('doctor')) {
        results.push({ type: 'Task', label: 'Reschedule appointment', color: '#EF4444', icon: '🗓️' });
        if (text.includes('mom') || text.includes('tell') || text.includes('call') || text.includes('wife') || text.includes('dad')) {
          const person = text.includes('mom') ? 'Mom' : text.includes('wife') ? 'Wife' : text.includes('dad') ? 'Dad' : 'Contact';
          results.push({ type: 'Reminder', label: `Message ${person}`, color: '#3B82F6', icon: '💬' });
        }
      }
      // Pattern 2: Shopping / Groceries
      else if (text.includes('buy') || text.includes('milk') || text.includes('grocery') || text.includes('store') || text.includes('eggs')) {
        results.push({ type: 'List Update', label: 'Added to Groceries', color: '#10B981', icon: '🛒' });
      }
      // Pattern 3: Ideas / Startups
      else if (text.includes('idea') || text.includes('app') || text.includes('startup') || text.includes('feature')) {
        results.push({ type: 'Idea', label: 'Idea safely archived', color: '#8B5CF6', icon: '💡' });
        if (text.includes('tomorrow') || text.includes('team') || text.includes('talk')) {
          results.push({ type: 'Task', label: 'Discuss with team', color: '#F59E0B', icon: '🗣️' });
        }
      }
      // Pattern 4: Deadlines / Urgency
      else if (text.includes('due') || text.includes('report') || text.includes('client') || text.includes('boss')) {
        results.push({ type: 'Urgent Task', label: 'Client / Report Deadline', color: '#EF4444', icon: '⚠️' });
      }
      // Pattern 5: Catch-all Fallback (Graceful)
      else {
        const words = liveInput.trim().split(' ');
        if (words.length > 4) {
          results.push({ type: 'Task', label: words.slice(0, 3).join(' ') + '...', color: '#3B82F6', icon: '✅' });
          results.push({ type: 'Note', label: 'Context preserved', color: '#8B5CF6', icon: '📝' });
        } else {
          results.push({ type: 'Action Item', label: liveInput.trim(), color: '#10B981', icon: '✅' });
        }
      }

      setMockResults(results);
      setDemoState('resolved');
    }, 3200);
  };

  // --------- WAITLIST STATE ---------
  const [email, setEmail] = useState('');
  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false);
  const [waitlistState, setWaitlistState] = useState('idle'); // idle -> success
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (waitlistState === 'success') {
      const timer = setTimeout(() => setShowSharePrompt(true), 3500);
      return () => clearTimeout(timer);
    }
  }, [waitlistState]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsWaitlistLoading(true);
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') { // Postgres unique violation error
          toast.success("You're already on the waitlist! We'll be in touch.");
          setWaitlistState('success');
        } else {
          console.error(error);
          toast.error('Something went wrong. Please try again.');
        }
      } else {
        setWaitlistState('success');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsWaitlistLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = 'Just joined the waitlist for Neuron - an AI that turns voice chaos into organized tasks!';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      toast.success('Link copied!');
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTwitterShare = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  const handleLinkedInShare = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');

  return (
    <footer
      data-testid="footer-section"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: '6vh',
        // Make the background purely dark so the fade to black feels natural
        backgroundColor: '#000000',
      }}
    >
      {/* Dim overlay for post-signup cinematic moment */}
      <AnimatePresence>
        {waitlistState === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 z-0 bg-black/90 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Subtle top separator */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)',
      }} />

      {/* Ambient Radial Glow / Breathing Pulse */}
      <motion.div
        style={{
          position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
          width: 800, height: 500, pointerEvents: 'none',
          background: 'radial-gradient(ellipse, rgba(139,92,246,0.05) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
        animate={waitlistState === 'success' ? {
          opacity: [0, 0.4, 0],
          scale: [0.8, 1.2, 0.8]
        } : {}}
        transition={waitlistState === 'success' ? {
          duration: 4, ease: "easeInOut", times: [0, 0.5, 1]
        } : {}}
      />

      {/* ── THE SHARED TAGLINE ── */}
      <motion.div
        style={{
          width: '100%', textAlign: 'center', padding: '4vh 1.5rem 4vh', flexShrink: 0,
          position: 'relative', zIndex: 10
        }}
        animate={{ opacity: waitlistState === 'success' ? 0 : 1 }}
        transition={{ duration: 1 }}
      >
        <p
          style={{
            fontSize: 'clamp(1.2rem, 2.2vw, 2.2rem)',
            fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1.3, whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#EDEDED' }}>Stop losing thoughts. </span>
          <span style={{ color: '#8B5CF6', textShadow: '0 0 30px rgba(139,92,246,0.4)' }}>
            Start living with clarity.
          </span>
        </p>
      </motion.div>

      {/* ── BODY CONTENT ── */}
      <div
        className="flex-1 flex flex-col items-center justify-center w-full max-w-4xl px-6 text-center relative z-10"
      >
        <AnimatePresence mode="wait">
          {waitlistState === 'idle' ? (
            <motion.div
              key="content-idle"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20, transition: { duration: 0.5 } }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="w-full flex flex-col items-center"
            >

              {/* --------- LIVE DEMO INTERFACE --------- */}
              <div className="w-full max-w-lg mb-20 relative">
                {demoState === 'idle' && (
                  <form onSubmit={(e) => { e.preventDefault(); handleParseDemo(); }} className="relative group">
                    <input
                      type="text"
                      value={liveInput}
                      onChange={(e) => setLiveInput(e.target.value)}
                      placeholder="Type anything. Seriously, anything."
                      autoComplete="off"
                      className="w-full bg-[#080808] border border-[#1A1A1A] text-[#EDEDED] pl-6 pr-12 py-5 rounded-2xl outline-none font-mono text-[13px] tracking-wide transition-all duration-300 focus:border-[#8B5CF6] focus:bg-[#0A0A0A] placeholder-[#444] shadow-[0_0_0_transparent] focus:shadow-[0_0_30px_rgba(139,92,246,0.1)]"
                    />
                    <button
                      type="submit"
                      disabled={!liveInput.trim()}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-[#151515] rounded-xl hover:bg-[#8B5CF6] transition-all duration-300 text-[#666] hover:text-white disabled:opacity-50 disabled:hover:bg-[#151515] disabled:hover:text-[#666]"
                    >
                      <ArrowRight size={16} />
                    </button>
                    {/* Floating hint label */}
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 text-[#555] text-[10px] font-mono tracking-widest uppercase opacity-60">
                      Try the demo
                    </div>
                  </form>
                )}

                {demoState === 'processing' && (
                  <div className="w-full bg-[#080808] border border-[#1A1A1A] px-6 py-5 rounded-2xl flex items-center justify-center gap-4">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-[2px] border-[#8B5CF6] border-t-transparent rounded-full" />
                    <span className="text-[13px] font-mono tracking-wide text-[#888]">Neuron is resolving...</span>
                  </div>
                )}

                {demoState === 'resolved' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }} className="flex flex-col gap-3">
                    {mockResults.map((res, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.15 + 0.1, duration: 0.4 }}
                        className="flex flex-row items-center gap-4 bg-[#080808] border border-[#1A1A1A] p-4 rounded-xl shadow-lg relative overflow-hidden"
                      >
                        {/* Subtle left border color hint */}
                        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: res.color }} />

                        <div className="w-10 h-10 rounded-full flex items-center justify-center ml-1" style={{ background: `${res.color}15`, color: res.color }}>
                          {res.icon}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: res.color }}>{res.type}</p>
                          <p className="text-[#EDEDED] text-sm font-medium mt-0.5 truncate">{res.label}</p>
                        </div>
                        <CheckCircle2 size={16} className="text-[#10B981] opacity-50 flex-shrink-0" />
                      </motion.div>
                    ))}
                    <button
                      onClick={() => { setDemoState('idle'); setLiveInput(''); }}
                      className="text-[11px] font-mono uppercase tracking-widest text-[#555] mt-4 hover:text-[#EDEDED] transition-colors"
                    >
                      ↺ Try another thought
                    </button>
                  </motion.div>
                )}
              </div>

              {/* --------- WAITLIST --------- */}
              <div className="w-full max-w-2xl">
                <p style={{ color: '#888888', marginBottom: '2.5rem', fontSize: '1rem' }}>
                  Your first thought is already waiting. Voice it. The rest takes care of itself.
                </p>

                <form
                  onSubmit={handleWaitlistSubmit}
                  style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}
                  data-testid="footer-form"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    style={{
                      background: '#0A0A0A',
                      border: '1px solid #1F1F1F',
                      color: '#EDEDED',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '0.5rem',
                      outline: 'none',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      width: '280px',
                      transition: 'border-color 0.3s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8B5CF6'}
                    onBlur={(e) => e.target.style.borderColor = '#1F1F1F'}
                    data-testid="footer-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={isWaitlistLoading}
                    className="bg-white text-black hover:bg-zinc-200 transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] font-medium px-8 py-3.5 rounded-full text-sm tracking-wide"
                    data-testid="footer-submit-button"
                  >
                    {isWaitlistLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          style={{ width: 16, height: 16, border: '2px solid black', borderTopColor: 'transparent', borderRadius: '50%' }}
                        />
                        Joining...
                      </span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        Join Waitlist <ArrowRight style={{ width: 16, height: 16 }} />
                      </span>
                    )}
                  </Button>
                </form>
              </div>

            </motion.div>
          ) : (

            /* --------- SUCCESS STATE (Quiet Mind cinematic ending) --------- */
            <motion.div
              key="content-success"
              className="text-center w-full max-w-xl mx-auto"
              data-testid="footer-success-state"
            >
              <div className="space-y-6">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="text-3xl lg:text-4xl font-light tracking-tight text-[#EDEDED]"
                >
                  Your brain just got lighter.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.5, delay: 2.0 }}
                  className="space-y-2 text-[#777] font-light leading-relaxed"
                >
                  <p>We'll reach out the moment Neuron is ready.</p>
                  <p>Until then — notice how good it feels to have let something go.</p>
                </motion.div>
              </div>

              {/* Share Prompt (Fades' in later to respect the quiet moment) */}
              <AnimatePresence>
                {showSharePrompt && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="pt-16 mt-16 border-t border-[rgba(255,255,255,0.05)]"
                  >
                    <p className="text-[#888] text-sm mb-6 uppercase tracking-widest font-mono">
                      Know someone drowning in their own head?
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <button onClick={handleTwitterShare} className="w-12 h-12 rounded-full border border-[#1A1A1A] bg-[#0A0A0A] flex items-center justify-center text-[#888] hover:text-[#EDEDED] hover:border-[#333] transition-colors" aria-label="Share on Twitter">
                        <Twitter size={18} />
                      </button>
                      <button onClick={handleLinkedInShare} className="w-12 h-12 rounded-full border border-[#1A1A1A] bg-[#0A0A0A] flex items-center justify-center text-[#888] hover:text-[#EDEDED] hover:border-[#333] transition-colors" aria-label="Share on LinkedIn">
                        <Linkedin size={18} />
                      </button>
                      <button onClick={handleCopyLink} className="w-12 h-12 rounded-full border border-[#1A1A1A] bg-[#0A0A0A] flex items-center justify-center text-[#888] hover:text-[#EDEDED] hover:border-[#333] transition-colors" aria-label="Copy link">
                        {copied ? <Check size={18} className="text-[#10B981]" /> : <Copy size={18} />}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Copyright */}
      <motion.div
        className="pt-8 mt-auto w-full max-w-4xl text-center relative z-10"
        animate={{ opacity: waitlistState === 'success' ? 0.3 : 1 }}
        transition={{ duration: 1.5 }}
      >
        <p className="text-[13px] text-[#666] mb-4 font-light italic tracking-wide">
          Built by people who got tired of losing their own best thoughts.
        </p>
        <p className="text-[11px] font-mono text-[#333] uppercase tracking-widest">
          © 2026 Neuron. All rights reserved.
        </p>
      </motion.div>
    </footer>
  );
};

export default Footer;
